import { motion } from 'framer-motion';
import { usePrefersReducedMotion } from '../../hooks/usePrefersReducedMotion';

type SuccessSealingProps = {
  size?: number;
};

/**
 * Animação cerimonial: a moldura dourada se desenha (path stroke),
 * um selo central cai e dá um pulso final de glow.
 * Respeita prefers-reduced-motion (mostra direto o estado final).
 */
export function SuccessSealing({ size = 220 }: SuccessSealingProps) {
  const reduced = usePrefersReducedMotion();
  const drawTransition = reduced
    ? { duration: 0 }
    : { duration: 0.9, ease: [0.65, 0, 0.35, 1] as const };
  const sealTransition = reduced
    ? { duration: 0 }
    : { delay: 0.6, duration: 0.7, ease: [0.16, 1, 0.3, 1] as const };

  return (
    <div
      className="relative mx-auto"
      style={{ width: size, height: size }}
      aria-hidden="true"
    >
      <svg viewBox="0 0 200 200" className="absolute inset-0">
        <defs>
          <linearGradient id="sealing-gold" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#E8D5A8" />
            <stop offset="0.5" stopColor="#D4AF37" />
            <stop offset="1" stopColor="#8B6F2C" />
          </linearGradient>
          <radialGradient id="sealing-glow" cx="0.5" cy="0.5" r="0.5">
            <stop offset="0" stopColor="rgba(212,175,55,0.55)" />
            <stop offset="1" stopColor="rgba(212,175,55,0)" />
          </radialGradient>
        </defs>

        {/* Glow ambiente */}
        <motion.circle
          cx="100"
          cy="100"
          r="92"
          fill="url(#sealing-glow)"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.8, duration: 1.2 }}
        />

        {/* Moldura externa que se desenha */}
        <motion.rect
          x="10"
          y="10"
          width="180"
          height="180"
          rx="4"
          stroke="url(#sealing-gold)"
          strokeWidth="3"
          fill="none"
          initial={{ pathLength: 0, opacity: 0.7 }}
          animate={{ pathLength: 1, opacity: 1 }}
          transition={drawTransition}
        />

        {/* Moldura interna fina */}
        <motion.rect
          x="22"
          y="22"
          width="156"
          height="156"
          rx="2"
          stroke="#D4AF37"
          strokeWidth="0.8"
          strokeOpacity="0.55"
          fill="none"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ ...drawTransition, delay: 0.15 }}
        />

        {/* Selo central */}
        <motion.g
          initial={{ opacity: 0, scale: 1.2, y: -8 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={sealTransition}
        >
          <circle cx="100" cy="100" r="32" fill="url(#sealing-gold)" />
          <circle cx="100" cy="100" r="28" fill="none" stroke="#0B0E1A" strokeOpacity="0.35" />
          <path
            d="M88 100 L97 109 L114 91"
            fill="none"
            stroke="#0B0E1A"
            strokeWidth="3.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </motion.g>
      </svg>
    </div>
  );
}
